import { ReactNode, useEffect, useState } from "react";

import { Icon } from "components/icon/Icon";
import Dropdown from "components/dropdown/Dropdown";

import "./Paginator.scss";

type PaginatorOption = {
  label: string;
  value: string;
};

export interface PaginatorProps {
  itemsLength: number;
  defaultItemsShownPerPage?: number;
  label?: ReactNode;
  showItemsLabel?: string;
  options?: PaginatorOption[];
  className?: string;
  presetActivePage?: number;
  onSelectPage?: (pageNumber: number) => void;
  onChangeShownItems?: (itemsShownNumber: number) => void;
  showPageNumbers?: boolean;
  showLastPage?: boolean;
}

const defaultPaginatorOptions = [
  { label: "5", value: "5" },
  { label: "10", value: "10" },
  { label: "15", value: "15" },
  { label: "20", value: "20" },
  { label: "50", value: "50" }
];

function Paginator({
  itemsLength,
  label,
  showItemsLabel = "Visa",
  defaultItemsShownPerPage = 15,
  options = defaultPaginatorOptions,
  className = "",
  presetActivePage = 1,
  onSelectPage,
  onChangeShownItems,
  showLastPage,
  showPageNumbers = false
}: PaginatorProps) {
  const [activePage, setActivePage] = useState<number>(presetActivePage);
  const [itemsShownPerPage, setItemsShowPerPage] = useState<number>(
    defaultItemsShownPerPage
  );
  const [lastPage, setLastPage] = useState(
    Math.ceil(itemsLength / itemsShownPerPage)
  );

  useEffect(() => {
    setLastPage(Math.ceil(itemsLength / itemsShownPerPage));
  }, [itemsLength, itemsShownPerPage]);

  useEffect(() => {
    setActivePage(presetActivePage);
  }, [presetActivePage]);

  function setPage(page: number) {
    setActivePage(page);
    onSelectPage?.(page);
  }

  function setShown(itemsNumber: number) {
    setItemsShowPerPage(itemsNumber);
    onChangeShownItems?.(itemsNumber);
    if (activePage === lastPage) {
      const newLastPage = Math.ceil(itemsLength / itemsNumber);
      if (newLastPage < lastPage) {
        setPage(newLastPage);
      }
    }
  }

  const getArrowsClassName = (refPage: number) => {
    return activePage === refPage ? "text-disabled" : "cursor-pointer";
  };

  return (
    <div className={`d-flex justify-content-between ${className}`}>
      {label && (
        <div>
          <span className="text-gray">{label}:</span> {itemsLength}
        </div>
      )}
      <div className="d-flex">
        <div className="d-flex align-items-center pr-3">
          <Icon
            name="angle-double-left"
            onClick={() => setPage(1)}
            className={`${getArrowsClassName(1)} px-3`}
          />

          <Icon
            name="angle-left"
            onClick={() => activePage > 1 && setPage(activePage - 1)}
            className={`${getArrowsClassName(1)} px-3`}
          />

          {showPageNumbers && (
            <>
              {activePage > 1 && (
                <div
                  onClick={() => setPage(activePage - 1)}
                  className="px-3 cursor-pointer"
                >
                  {activePage - 1}
                </div>
              )}

              {activePage < lastPage && (
                <div className="px-3 active-page">{activePage}</div>
              )}

              {activePage + 1 < lastPage && (
                <div
                  onClick={() => setPage(activePage + 1)}
                  className="px-3 cursor-pointer"
                >
                  {activePage + 1}
                </div>
              )}

              <div onClick={() => setPage(activePage)} className="px-3">
                ...
              </div>
              <div
                onClick={() => setPage(lastPage)}
                className={`px-3 cursor-pointer ${
                  activePage === lastPage ? "active-page" : ""
                }`}
              >
                {lastPage}
              </div>
            </>
          )}

          <Icon
            name="angle-right"
            onClick={() => lastPage !== activePage && setPage(activePage + 1)}
            className={`${getArrowsClassName(lastPage)} px-3`}
          />

          {showLastPage && (
            <Icon
              name="angle-double-right"
              onClick={() => setPage(lastPage)}
              className={`${getArrowsClassName(lastPage)} px-3`}
            />
          )}

          <div className="vertical-separator ml-3"></div>
        </div>

        {options !== undefined && (
          <ShowNumberOfRows
            label={showItemsLabel}
            itemsShownPerPage={itemsShownPerPage.toString()}
            options={options}
            setShown={(v) => setShown(v)}
          />
        )}
      </div>
    </div>
  );
}
export default Paginator;

type ShowNumberOfRowsProps = {
  label: ReactNode | string;
  itemsShownPerPage: string;
  options: PaginatorOption[];
  setShown: (itemsShownNumber: number) => void;
};

function ShowNumberOfRows({
  itemsShownPerPage,
  label,
  options,
  setShown
}: ShowNumberOfRowsProps) {
  const onChange = (optionValue?: string): void => {
    if (optionValue) {
      setShown(parseInt(optionValue, 10));
    }
  };

  return (
    <div className="d-flex align-items-center">
      <span className="text-grey px-2">{label}:</span>
      <Dropdown
        value={itemsShownPerPage}
        options={options}
        onChange={(selected) => {
          if (selected && !Array.isArray(selected)) {
            const selectedOption = selected as { value: string; label: string };
            onChange(selectedOption.value);
          }
        }}
        className="paginator-dropdown"
      />
    </div>
  );
}
